import EventEmitter from 'eventemitter3'
import { nanoid } from 'nanoid'

const REPLY_TYPE_PREFIX = 'reply-'

/**
 * Manages a long-lived connection to the Wavebox extension.
 */
class BrainboxPort extends EventEmitter {
  #isConnected = false

  #port = undefined

  #portRequests = new Map()

  /* **************************************************************************/
  // MARK: Connection lifecycle
  /* **************************************************************************/

  constructor () {
    super()
  }

  /**
   * Open the extension bridge and attach its message listeners unless a port
   * is already held. The extension's connected message makes the bridge ready.
   * @param {string} extensionId - The Wavebox extension to connect to.
   */
  connect (extensionId) {
    if (this.#port) {
      return
    }

    if (!window.chrome?.runtime?.connect) {
      throw new Error('Chrome extension API not available')
    }

    if (!extensionId) {
      throw new Error('Extension ID is required')
    }

    this.#port = window.chrome.runtime.connect(extensionId, {
      name: 'BRAINBOX_CHAT_SYSTEM_BRIDGE_5'
    })

    this.#port.onDisconnect.addListener(this.handlePortDisconnect)
    this.#port.onMessage.addListener(this.handlePortMessage)
  }

  /**
   * Close the current port and clear its reference and connected state.
   */
  disconnect () {
    if (this.#port) {
      this.#port.disconnect()
      this.#port = undefined
      this.#isConnected = false
    }
  }

  get isConnected () {
    return this.#isConnected
  }

  /* **************************************************************************/
  // MARK: Message transport
  /* **************************************************************************/

  /**
   * Send a request with a unique reply ID and wait for the extension's response.
   * Reject when no port exists or the reply reports an error; no timeout is added.
   * @param {string} type - The extension request name.
   * @param {*} [data] - The payload sent with the request.
   * @returns {Promise<*>} The matching reply's data.
   */
  async request (type, data) {
    if (!this.#port) {
      throw new Error('Port not connected')
    }

    return new Promise((resolve, reject) => {
      const id = nanoid()
      this.#portRequests.set(id, [resolve, reject])
      this.#port.postMessage({
        type,
        reply: `${REPLY_TYPE_PREFIX}${id}`,
        data
      })
    })
  }

  /**
   * Send a one-way extension message, throwing if no port exists.
   * @param {string} type - The extension message name.
   * @param {*} [data] - The payload sent with the message.
   */
  send (type, data) {
    if (!this.#port) {
      throw new Error('Port not connected')
    }

    this.#port.postMessage({
      type,
      data
    })
  }

  /* **************************************************************************/
  // MARK: Extension operations
  /* **************************************************************************/

  /**
   * Ask the extension to convert a legacy chat and archive its source record.
   * @param {string} chatId - The legacy chat to convert.
   * @returns {Promise<*>} The extension's converted chat data.
   */
  async convertOldChat (chatId) {
    return this.request('convertOldChat', { chatId })
  }

  /**
   * Ask the extension to archive a legacy chat and wait for completion.
   * @param {string} chatId - The legacy chat to archive.
   * @returns {Promise<void>} Completion of the archive request.
   */
  async deleteOldChat (chatId) {
    return this.request('deleteOldChat', { chatId })
  }

  /**
   * Captures a screenshot using the Wavebox extension
   * @param {Object} options - Capture options
   * @param {string} options.regionInstructionText - Instruction text shown in region capture mode
   * @param {string} options.fullPageInstructionText - Instruction text shown in full page capture mode
   * @param {string} options.format - Image format (e.g., 'png')
   * @returns {Promise<{dataUrl: string, region?: Object}>} Screenshot data
   */
  async captureRegion (options = {}) {
    return this.request('captureRegion', options)
  }

  /**
   * Ask the extension for a Space, Group or App icon. Only for an extension
   * that lists `entityIcon` in its `bridge_features`; an older one rejects.
   * @param {Object} entity - What to draw.
   * @param {'space'|'group'|'app'} entity.kind - The kind of entity.
   * @param {string} entity.id - Its Wavebox id.
   * @param {number} [entity.size] - CSS pixels; the extension renders at 2x.
   * @returns {Promise<{dataUrl: string} | null>} The icon, or null when there is none.
   */
  async getEntityIcon ({ kind, id, size }) {
    return this.request('getEntityIcon', { kind, id, size })
  }

  /**
   * Ask the extension to take the user back to where a chat was started: the
   * tab if it is still open, else its App, Group or Space. Only for an
   * extension that lists `focusOrigin`.
   * @param {Object} target - Everything the chat remembers about its origin.
   * @param {number} [target.tabId] - The Chrome tab (this browser session only).
   * @param {string} [target.serviceTabId] - The Wavebox app tab.
   * @param {string} [target.appId] - The App.
   * @param {string} [target.groupId] - The Group.
   * @param {string} [target.spaceId] - The Space.
   * @returns {Promise<{focused: string}>} What the extension focused.
   */
  async focusOrigin (target) {
    return this.request('focusOrigin', target)
  }

  /**
   * Tell the extension about a chat it can list under its Group and App
   * (one-way; an extension without `chatIndex` ignores it).
   * @param {Object} entry - `{ id, title, engineId, spaceId, groupId, appId, updatedAt }`.
   */
  chatUpserted (entry) {
    this.send('chatUpserted', entry)
  }

  /**
   * Tell the extension a chat is gone (one-way).
   * @param {string} id - The deleted chat.
   */
  chatDeleted (id) {
    this.send('chatDeleted', { id })
  }

  /* **************************************************************************/
  // MARK: Port events
  /* **************************************************************************/

  handlePortDisconnect = () => {
    const wasConnected = this.#isConnected

    if (window.chrome?.runtime?.lastError) {
      console.error('[BrainboxPort] Disconnect reason:', window.chrome.runtime.lastError)
    }

    this.#isConnected = false
    this.emit('port-disconnected', wasConnected)
  }

  handlePortMessage = (evt) => {
    const { type, data, error } = evt

    if (type && type.startsWith(REPLY_TYPE_PREFIX)) {
      const id = type.substring(REPLY_TYPE_PREFIX.length)
      if (!this.#portRequests.has(id)) {
        console.error(`[BrainboxPort] Unhandled port message (reply) ${type}`)
        return
      }
      const [resolve, reject] = this.#portRequests.get(id)
      this.#portRequests.delete(id)
      error ? reject(error) : resolve(data)
    } else if (type === 'connected') {
      if (!this.#isConnected) {
        this.#isConnected = true
        this.emit('port-connected', data)
      }
    } else {
      this.emit(type, data)
      this.emit('port-message', type, data)
    }
  }
}

export default new BrainboxPort()
